import { gql, useMutation } from "@apollo/client";
import { Modal } from "components";
import { useFormik } from "formik";
import { useEffect } from "react";
import toast from "react-hot-toast";
import TicketForm from "./forms/create-ticket";
import { TicketFormSchema } from "./forms/create-ticket/schema";
import { useNavigate } from "react-location";
import { LocationGenerics } from "router/location";

const CREATE_COMPLAINT = gql`
  mutation CreateComplaint(
    $type: ID!
    $description: String
    $supportingDocumentUrls: [String]
    $servicePoint: ID
    $region: ID
    $district: ID
    $meta: ComplaintMetaInput
    $contactPerson: ContactPersonInput
    $source: String
    $sourceId: String
    $assignToSelf: Boolean
  ) {
    createComplaint(
      type: $type
      description: $description
      supportingDocumentUrls: $supportingDocumentUrls
      servicePoint: $servicePoint
      region: $region
      district: $district
      meta: $meta
      contactPerson: $contactPerson
      source: $source
      sourceId: $sourceId
      assignToSelf: $assignToSelf
    ) {
      _id
    }
  }
`;

export default function CreateComplaintContainer({
  open,
  setOpen,
  refetch,
  id,
  servicePoint
}: {
  id?: string;
  servicePoint?: any;
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const navigate = useNavigate<LocationGenerics>()
  const [createComplaint] = useMutation(CREATE_COMPLAINT, {
    refetchQueries: ["GetServicePointComplaints"]
  });

  const form = useFormik<any>({
    initialValues: {
      contactPersonInfo: {
        fullName: "",
        phoneNumber: "",
        emailAddress: "",
      },
      complaintInfo: {
        category: "",
        type: null,
        description: "",
        supportingDocumentUrls: [],
        assignToSelf: false,
        source: "",
        sourceId: "",
        region: "",
        district: "",
        location: "",
        ghanaPostAddress: "",
        poleNumber: ""
      },
    },
    validationSchema: TicketFormSchema,
    onSubmit: async (values) => {
      await createComplaint({
        variables: {
          ...values.complaintInfo,
          servicePoint: servicePoint?._id,
          contactPerson: values.contactPersonInfo,
          type: values.complaintInfo.type?._id,
        },
      }).then(({ data }) => {
        if (data.createComplaint._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Complaint Created Successfully",
            })
          );
          refetch?.();
          form.resetForm();
          if(values.complaintInfo?.assignToSelf) {
            navigate({to: `/tickets/${data.createComplaint._id}`})
          }
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not create Complaint",
            })
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    form.setFieldValue(
      "contactPersonInfo.emailAddress",
      servicePoint?.customerRepresentative?.emailAddress
    );
    form.setFieldValue(
      "contactPersonInfo.fullName",
      servicePoint?.customerRepresentative?.fullName
    );
    form.setFieldValue(
      "contactPersonInfo.phoneNumber",
      servicePoint?.customerRepresentative?.phoneNumber
    );
    form.setFieldValue(
      "complaintInfo.region",
      servicePoint?.region?._id
    );
    form.setFieldValue(
      "complaintInfo.district",
      servicePoint?.district?._id
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicePoint]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      size={"4xl"}
      title="Add New Ticket"
      description={`Create a new ticket for customer with SPN ${servicePoint?.code}.`}
      hidePadding={true}
      renderActions={() => (
        <button
          type="button"
          disabled={form.isSubmitting}
          onClick={form.submitForm}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-500 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          {form.isSubmitting ? "Creating..." : "Create Ticket"}
        </button>
      )}
    >
      <TicketForm form={form} />
    </Modal>
  );
}
